/* eslint-disable camelcase */
import { get } from 'lodash';

import React, { useContext, useEffect } from 'react';
import Logger from '../services/logger';
import RecruiterHttpService from '../api/RecruiterHttpService';
import EmployerRepHttpService from '../api/EmployerRepHttpService';
import EmployerHttpService from '../api/EmployerHttpService';
import SettingsContext from './SettingsContext';
import {
    TRACKING_OPTIONS_MAP,
    USER_OPTIONS_MAP,
    REJECTION_REASONS_MAP, TRACKING_EVENTS,
} from '../constants/TrackingConstants';
import { UserRoles } from '../constants/UserRole';
import LocalStorageService from '../services/LocalStorageService';
import {
    EVENTS_QUEUE,
    EVENTS_TO_TRACK_IMMEDIATELY,
} from '../constants/UserEvents';
import EventTrackingHTTPService from '../api/EventTrackingHTTPService';
import EmployerWorkflowStatus from '../constants/EmployerWorkflowStatus';
import {
    getNewRelicBrowserAPI,
    newRelicIdentifyUser,
    newRelicTrackEvent,
} from '../services/NewRelicTrackingService';

const TrackingContext = React.createContext({});

export const TrackingProvider = TrackingContext.Provider;
export const TrackingConsumer = TrackingContext.Consumer;

let currentUser;

const HOTJAR_TAGS_MAP = {};
const TRACKING_EVENTS_MAP = {};

const TRACKING_EVENTS_BY_WORKFLOW_STEP = {
    [EmployerWorkflowStatus.REVIEWED]: TRACKING_EVENTS.REVIEW_CANDIDATE_CLICKED,
};

const getCollectionWithLowerCasedKeys = (collection) => {
    const newCollection = {};

    for (const key in collection) {
        if (key in collection) {
            newCollection[key.toLowerCase()] = collection[key];
        }
    }

    return newCollection;
};

const TRACKING_OPTIONS_MAP_LOWER = getCollectionWithLowerCasedKeys(TRACKING_OPTIONS_MAP);
const USER_OPTIONS_MAP_LOWER = getCollectionWithLowerCasedKeys(USER_OPTIONS_MAP);

const getMappedOptions = (options, mapCollection) => {
    if (!options) {
        return {};
    }

    const mappedOptions = {};

    for (const key in options) {
        if (key in options) {
            const loweKey = key.toLowerCase();
            const trackingParamName = mapCollection[loweKey];

            let item = options[key];

            if (loweKey === 'rejectreasons') {
                item = options[key].map((reason) => REJECTION_REASONS_MAP[reason]).join();
            }

            if (trackingParamName) {
                mappedOptions[trackingParamName] = item;
            }
        } else {
            Logger.error(`Tracking  property -- ${key} -- not found`);
        }
    }

    return mappedOptions;
};

const getMappedUserOptions = () => ({
    ...getMappedOptions({
        ...currentUser,
    }, USER_OPTIONS_MAP_LOWER),
});

const identifyUser = () => {
    const userId = currentUser?._id;
    if (userId && getNewRelicBrowserAPI()) {
        const userToTrack = getMappedUserOptions();

        newRelicIdentifyUser({
            ...userToTrack,
            userId,
            environment: ENVIRONMENT,
        });

        window.hj('identify', userId, {
            ...userToTrack,
        });
    }
};

// eslint-disable-next-line consistent-return
const trackEvent = async (eventName, options) => {
    try {
        const event = TRACKING_EVENTS_MAP[eventName];
        if (event) {
            const mappedOptions = getMappedOptions(options, TRACKING_OPTIONS_MAP_LOWER);
            let employerName;

            if (currentUser) {
                const mappedUserOptions = getMappedUserOptions();
                employerName = mappedUserOptions[USER_OPTIONS_MAP.employerName];
            }

            const employerId = options?.employerId;
            if (!employerName && employerId) {
                const employerData = await EmployerHttpService.getEmployer(employerId);
                employerName = employerData?.name;
            }

            newRelicTrackEvent(event, {
                ...mappedOptions,
                ...(employerName && { [USER_OPTIONS_MAP.employerName]: employerName }),
                environment: ENVIRONMENT,
            });
        }
    } catch (err) {
        Logger.error(err.message);
    }
};
// eslint-disable-next-line consistent-return
const trackTag = async (tags) => {
    try {
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve) => {
            if (currentUser) {
                const tagsToTrack = tags.reduce((acc, tag) => {
                    const hotjarTag = HOTJAR_TAGS_MAP[tag];
                    if (hotjarTag) {
                        acc.push(hotjarTag);
                    }

                    return acc;
                }, []);

                const t = [...tagsToTrack, ENVIRONMENT];
                window.hj('tagRecording', t);
                resolve();
            }
        });
    } catch (err) {
        Logger.error(err.message);
    }
};

const isAutomation = (user) => {
    const email = user.id || user.email;
    const [name] = email.split('@');

    return name.indexOf('automation') !== -1;
};

/**
 * This is used to store user for using it in New Relic system,
 * should be called first
 * on the start of React app before tracking any events
 */
// eslint-disable-next-line default-param-last
const initUserTracking = async (user = {}, isTalenyaUser) => {
    try {
        const hasTERole = (user.roles || []).includes('TE');
        const entityId = get(user, 'userPrefs.entityId');
        const _id = get(user, 'origUserPrefs._id', null);

        if (isAutomation(user)) {
            return;
        }

        if (hasTERole && entityId) {
            const recruiter = await RecruiterHttpService.getRecruiter(entityId);
            const {
                _id: recruiterId,
                created,
                fullName,
            } = recruiter;

            if (!currentUser) {
                currentUser = {
                    ...user,
                    recruiterId,
                    created,
                    fullName,
                    roles: user.roles.join(),
                    isTalenyaUser,
                    name: fullName || getUserName(user, user.user_id.startsWith('linkedin|')),
                    email: user.id || user.email,
                    _id,
                };

                identifyUser();
            }
        } else {
            const existingEmployerRepId = user.impersonation.realEntityRole === UserRoles.employerRep
                && user.impersonation.realEntityId;

            const employerRep = existingEmployerRepId
                ? await EmployerRepHttpService.getEmployerRep(existingEmployerRepId)
                : await EmployerRepHttpService.getEmployerRepsByEmail(user.email, true);

            if (!employerRep) {
                return;
            }

            const {
                employerId,
                _id: employerRepId,
                created,
                name: fullName,
                roles,
            } = employerRep;

            const { name: employerName = null, office = null } = employerId
                ? await EmployerHttpService.getEmployer(employerId) || {}
                : {};

            if (!currentUser) {
                currentUser = {
                    ...user,
                    employerId,
                    employerRepId,
                    created,
                    employerName: checkUserHasTalenyaEmail(user) ? 'Talenya' : employerName,
                    fullName,
                    roles: roles.join(),
                    office,
                    isTalenyaUser,
                    name: fullName || getUserName(user, user.user_id.startsWith('linkedin|')),
                    email: user.id || user.email,
                    _id,
                };

                identifyUser();
            }
        }
    } catch (err) {
        Logger.error(err.message);
    }

    function getUserName(currUser, isLinkedin) {
        return isLinkedin
            ? currUser.name
            : (currUser.fullName || currUser.email || currUser.id);
    }
};

function checkUserHasTalenyaEmail(user) {
    return user && user.email && user.email.endsWith('talenya.com');
}

class UserEvent {
    constructor(eventData) {
        this.timestamp = new Date();
        this.employerId = eventData.employerId;
        this.positionId = eventData.positionId;
        this.type = eventData.type;
        this.recruiterId = eventData.recruiterId;
        this.employerRepId = eventData.employerRep || null;
        this.data = eventData.data;
    }
}

const handleUserEvent = (eventData) => {
    if (!eventData) {
        Logger.error('EMPTY EVENT DATA!');
        return false;
    }

    trackUserEvent(eventData);

    const event = new UserEvent(eventData);

    if (EVENTS_TO_TRACK_IMMEDIATELY.includes(event.type)) {
        return EventTrackingHTTPService.createEventBulk({ events: [event] });
    }

    const trackingEventsArray = LocalStorageService.getItem(EVENTS_QUEUE.EVENTS_QUEUE_NAME);

    if (!trackingEventsArray) {
        LocalStorageService.setItem(EVENTS_QUEUE.EVENTS_QUEUE_NAME, [event]);
    } else if (trackingEventsArray.length >= EVENTS_QUEUE.EVENTS_QUEUE_LENGTH) {
        trackingEventsArray.push(event);
        LocalStorageService.removeItem(EVENTS_QUEUE.EVENTS_QUEUE_NAME);
        return EventTrackingHTTPService.createEventBulk({ events: trackingEventsArray });
    } else {
        trackingEventsArray.push(event);
        LocalStorageService.setItem(EVENTS_QUEUE.EVENTS_QUEUE_NAME, trackingEventsArray);
    }

    return true;
};

const trackUserEvent = ({
    workflowStep,
    currentCandidate,
    getExplain,
    positionId,
    position,
}) => {
    const eventName = TRACKING_EVENTS_BY_WORKFLOW_STEP[workflowStep];

    if (!eventName) {
        return;
    }

    const explain = getExplain(currentCandidate.talentId);
    const matchScore = currentCandidate.matchScore || (explain && explain.score);

    trackEvent(eventName, {
        positionId,
        candidateName: currentCandidate.name,
        talentId: currentCandidate.talentId,
        predictionScore: currentCandidate.predictionScore,
        matchScore,
        positionName: position.jobTitle,
        employerId: position.employerId,
    });
};

export const TrackingApp = ({ children }) => {
    const {
        systemPreferences: {
            AnalyticsTrackingEvents = {},
            HotjarAnalyticsTags = {},
        },
    } = useContext(SettingsContext);

    useEffect(() => {
        if (!Object.keys(TRACKING_EVENTS_MAP).length) {
            for (const key in AnalyticsTrackingEvents) {
                if (key in AnalyticsTrackingEvents) {
                    TRACKING_EVENTS_MAP[key] = AnalyticsTrackingEvents[key];
                }
            }
        }
        if (!Object.keys(HOTJAR_TAGS_MAP).length) {
            for (const key in HotjarAnalyticsTags) {
                if (key in HotjarAnalyticsTags) {
                    HOTJAR_TAGS_MAP[key] = HotjarAnalyticsTags[key];
                }
            }
        }
    }, [AnalyticsTrackingEvents, HotjarAnalyticsTags]);

    return (
        <TrackingProvider value={{
            trackEvent,
            initUserTracking,
            trackTag,
            handleUserEvent,
        }}>
            {children}
        </TrackingProvider>
    );
};

export default TrackingContext;
